import { useMemo, useState } from "react";
import {
    FaChevronUp, FaChevronDown, FaDollarSign, FaPlus, FaCreditCard,
    FaGift, FaUndo, FaClock, FaExchangeAlt, FaRedoAlt
} from "react-icons/fa";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/airbnb.css";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { toast } from "react-toastify";
import CargoRecurrente from "@/components/financiero/balances/CargoRecurrente";

/* ============ Utils cortos ============ */
const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => {
    if (!d) return "";
    const x = new Date(d);
    return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};
const toISODateStart = (dayStr) => (dayStr ? `${dayStr} 00:00:00` : null);
const toSQLDateTime = (d) => {
    if (!d) return null;
    const x = new Date(d);
    return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())} ${pad(x.getHours())}:${pad(x.getMinutes())}:00`;
};
const HOY = fmtDate(new Date());
const money = (v) =>
    `$${Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ============ Inputs reusables ============ */
function AmountInput({ value, onChange, placeholder = "Monto", allowNegative = false, maxDecimals = 2, className = "" }) {
    const strip = (s) => {
        const base = allowNegative ? /[^\d.\-]/g : /[^\d.]/g;
        s = String(s ?? "").replace(base, "");
        const parts = s.split(".");
        if (parts.length > 2) s = parts[0] + "." + parts.slice(1).join("").replace(/\./g, "");
        if (allowNegative) s = s.replace(/(?!^)-/g, ""); else s = s.replace(/-/g, "");
        if (maxDecimals >= 0 && s.includes(".")) {
            const [ent, dec] = s.split(".");
            s = ent + "." + dec.slice(0, maxDecimals);
        }
        return s;
    };
    const withCommas = (raw) => {
        if (raw === "" || raw === "-" || raw === "." || raw === "-.") return raw;
        const neg = raw.startsWith("-");
        if (neg) raw = raw.slice(1);
        let [ent, dec = ""] = raw.split(".");
        ent = ent.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return neg ? `-${dec ? `${ent}.${dec}` : ent}` : (dec ? `${ent}.${dec}` : ent);
    };
    const display = withCommas(strip(value ?? ""));

    return (
        <input
            type="text"
            inputMode="decimal"
            className={`w-full rounded border px-3 py-2 ${className}`}
            placeholder={placeholder}
            value={display}
            onChange={(e) => onChange(strip(e.target.value))}
            onBlur={(e) => {
                let raw = strip(e.target.value);
                if (["", "-", ".", "-."].includes(raw)) return onChange("");
                raw = raw.replace(/\.$/, "");
                if (maxDecimals >= 0) raw = Number(raw || 0).toFixed(maxDecimals);
                onChange(raw);
            }}
            onKeyDown={(e) => {
                if (["e", "E", "+"].includes(e.key)) e.preventDefault();
                if (!allowNegative && e.key === "-") e.preventDefault();
            }}
        />
    );
}
const DateDay = ({ value, onChange }) => (
    <Flatpickr
        className="w-full rounded border px-3 py-2"
        options={{ enableTime: false, altInput: true, altFormat: "d/m/Y", dateFormat: "Y-m-d", maxDate: "today" }}
        value={value}
        onChange={([d]) => onChange(d ? fmtDate(d) : "")}
        placeholder="Fecha (día)"
    />
);
const DateTime = ({ value, onChange, minNow = true }) => (
    <Flatpickr
        className="w-full rounded border px-3 py-2"
        options={{
            enableTime: true, time_24hr: true,
            altInput: true, altFormat: "d/m/Y H:i", dateFormat: "Y-m-d H:i",
            ...(minNow ? { minDate: new Date() } : {})
        }}
        value={value || null}
        onChange={([d]) => onChange(d ? `${fmtDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}` : "")}
        placeholder="Fecha y hora"
    />
);
const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-sm text-slate-700 font-medium">{label}</span>
        <div className="mt-1">{children}</div>
    </label>
);

/* ============ Schema de operaciones ============ */
const OPS = (empresaId, clientes, productos) => ({
    manual_payment: {
        icon: FaDollarSign, color: "bg-green-100",
        label: "Manual Payment", description: "Registro manual de pago (abono al depósito).",
        fields: [
            { key: "amount", type: "money", props: {} },
            { key: "date", type: "day", props: {} },
            { key: "visible_comment", type: "text", props: { placeholder: "Comentario visible", className: "sm:col-span-2" } },
            { key: "internal_comment", type: "text", props: { placeholder: "Comentario interno", className: "sm:col-span-2" } },
        ],
        isFilled: (s) => Number(s.amount || 0) > 0,
        build: (s) => ({
            url: API_ROUTES.depositoEmpresa,
            body: {
                empresa_id: empresaId,
                naturaleza: "abono",
                monto: Number(s.amount),
                comentario: s.visible_comment || "Pago manual",
                comentario_interno: s.internal_comment || null,
                fecha_registro_pago: toISODateStart(s.date || HOY),
            },
        }),
        call: ({ url, body }) => axios.post(url, body, { withCredentials: true }),
    },

    manual_charge: {
        icon: FaPlus, color: "bg-red-100",
        label: "Manual Charge", description: "Cargo manual.",
        fields: [
            { key: "amount", type: "money", props: {} },
            { key: "date", type: "day", props: {} },
            { key: "service", type: "text", props: { placeholder: "Servicio / concepto" } },
            { key: "visible_comment", type: "text", props: { placeholder: "Comentario visible", className: "sm:col-span-2" } },
            { key: "internal_comment", type: "text", props: { placeholder: "Comentario interno", className: "sm:col-span-2" } },
        ],
        isFilled: (s) => Number(s.amount || 0) > 0,
        build: (s) => ({
            url: API_ROUTES.depositoEmpresa,
            body: {
                empresa_id: empresaId,
                naturaleza: "cargo",
                monto: Number(s.amount),
                comentario: s.visible_comment || `Cargo manual${s.service ? ` [${s.service}]` : ""}`,
                comentario_interno: s.internal_comment || null,
                fecha_registro_pago: toISODateStart(s.date || HOY),
            },
        }),
        call: ({ url, body }) => axios.post(url, body, { withCredentials: true }),
    },

    manual_credit: {
        icon: FaCreditCard, color: "bg-blue-100",
        label: "Manual Credit", description: "Aumenta el límite de crédito (+).",
        fields: [
            { key: "amount", type: "money", props: {} },
            { key: "date", type: "day", props: {} },
            { key: "visible_comment", type: "text", props: { placeholder: "Comentario visible", className: "sm:col-span-2" } },
            { key: "internal_comment", type: "text", props: { placeholder: "Comentario interno", className: "sm:col-span-2" } },
        ],
        isFilled: (s) => Number(s.amount || 0) > 0,
        build: async (s) => {
            const { data: resumen } = await axios.get(API_ROUTES.resumenEmpresa(empresaId), { withCredentials: true });
            const current = Number(resumen?.credito_base ?? resumen?.credito_limite ?? 0);
            return {
                url: API_ROUTES.setLimiteEmpresa,
                body: {
                    empresa_id: empresaId,
                    credito_base: current + Number(s.amount || 0),
                    comentario: s.visible_comment || "Ajuste manual de límite (+)",
                    comentario_interno: s.internal_comment || null,
                    fecha_registro_pago: toISODateStart(s.date || HOY),
                },
            };
        },
        call: ({ url, body }) => axios.post(url, body, { withCredentials: true }),
    },

    promotional_credit: {
        icon: FaGift, color: "bg-purple-100",
        label: "Promotional Credit", description: "Crédito extra (independiente del límite).",
        fields: [
            { key: "amount", type: "money", props: {} },
            { key: "date", type: "day", props: {} },
            { key: "visible_comment", type: "text", props: { placeholder: "Comentario visible", className: "sm:col-span-2" } },
            { key: "internal_comment", type: "text", props: { placeholder: "Comentario interno", className: "sm:col-span-2" } },
        ],
        isFilled: (s) => Number(s.amount || 0) > 0,
        build: (s) => ({
            url: API_ROUTES.extraEmpresa,
            body: {
                empresa_id: empresaId, tipo: "promocional",
                monto: Number(s.amount || 0),
                comentario: s.visible_comment || "Crédito promocional",
                comentario_interno: s.internal_comment || null,
                fecha_registro_pago: toISODateStart(s.date || HOY),
            },
        }),
        call: ({ url, body }) => axios.post(url, body, { withCredentials: true }),
    },

    refund: {
        icon: FaUndo, color: "bg-yellow-100",
        label: "Refund", description: "Reembolso (resta del depósito).",
        fields: [
            { key: "amount", type: "money", props: {} },
            { key: "date", type: "day", props: {} },
            { key: "visible_comment", type: "text", props: { placeholder: "Comentario visible", className: "sm:col-span-2" } },
            { key: "internal_comment", type: "text", props: { placeholder: "Comentario interno", className: "sm:col-span-2" } },
        ],
        isFilled: (s) => Number(s.amount || 0) > 0,
        build: (s) => ({
            url: API_ROUTES.depositoEmpresa,
            body: {
                empresa_id: empresaId, naturaleza: "cargo",
                monto: Number(s.amount || 0),
                comentario: s.visible_comment || "Reembolso",
                comentario_interno: s.internal_comment || null,
                fecha_registro_pago: toISODateStart(s.date || HOY),
            },
        }),
        call: ({ url, body }) => axios.post(url, body, { withCredentials: true }),
    },

    temp_credit_limit: {
        icon: FaClock, color: "bg-indigo-100",
        label: "Crédito temporal", description: "Suma temporal al crédito (expira en fecha/hora).",
        fields: [
            { key: "amount", type: "money", props: { placeholder: "Monto temporal" } },
            { key: "end", type: "datetime", props: {} },
            { key: "visible_comment", type: "text", props: { placeholder: "Comentario visible", className: "sm:col-span-2" } },
            { key: "internal_comment", type: "text", props: { placeholder: "Comentario interno", className: "sm:col-span-2" } },
        ],
        isFilled: (s) => Number(s.amount || 0) > 0 && !!s.end,
        build: (s) => {
            const end = new Date(s.end);
            if (!(end > new Date())) throw new Error("La fecha de fin debe ser futura.");
            return {
                url: API_ROUTES.extraEmpresa,
                body: {
                    empresa_id: empresaId, tipo: "temporal",
                    monto: Number(s.amount || 0),
                    fecha_fin: toSQLDateTime(end),
                    comentario: s.visible_comment || "Crédito temporal",
                    comentario_interno: s.internal_comment || null,
                },
            };
        },
        call: ({ url, body }) => axios.post(url, body, { withCredentials: true }),
    },

    deposit_transfer: {
        icon: FaExchangeAlt, color: "bg-orange-100",
        label: "Transferencia de Depósito", description: "Mueve depósito entre clientes.",
        fields: [
            { key: "from_cliente_id", type: "select", props: { options: (clientes || []).map(c => ({ v: c.id, t: `${c.nombre} — Depósito: ${money(c.deposito_asignado)}` })), placeholder: "Cliente origen" } },
            { key: "to_cliente_id", type: "select", props: { options: (clientes || []).map(c => ({ v: c.id, t: c.nombre })), placeholder: "Cliente destino" } },
            { key: "amount", type: "money", props: { placeholder: "Monto a transferir" } },
            { key: "comment", type: "text", props: { placeholder: "Comentario (opcional)", className: "sm:col-span-2" } },
        ],
        isFilled: (s) => Number(s.amount || 0) > 0 && s.from_cliente_id && s.to_cliente_id,
        build: (s) => {
            if (String(s.from_cliente_id) === String(s.to_cliente_id)) {
                throw new Error("Origen y destino no pueden ser iguales.");
            }
            return {
                url: API_ROUTES.transferirDeposito,
                body: {
                    empresa_id: empresaId,
                    from_scope: "cliente", from_cliente_id: Number(s.from_cliente_id),
                    to_scope: "cliente", to_cliente_id: Number(s.to_cliente_id),
                    tipo: "cliente_a_cliente",
                    monto: Number(s.amount || 0),
                    comentario: s.comment || "Transferencia de depósito",
                },
            };
        },
        call: ({ url, body }) => axios.post(url, body, { withCredentials: true }),
    },
});

/* ============ Renderizador genérico de campos ============ */
function FormRenderer({ schema, state, setState, clientes }) {
    const set = (k, v) => setState((p) => ({ ...p, [k]: v }));
    return (
        <div className="p-4 border-t bg-gray-50">
            <div className="grid sm:grid-cols-2 gap-3">
                {schema.fields.map(({ key, type, props }) => {
                    if (type === "money") return (
                        <Field key={key} label={props?.label || "Importe"}>
                            <AmountInput value={state[key]} onChange={(v) => set(key, v)} {...props} />
                        </Field>
                    );
                    if (type === "day") return (
                        <Field key={key} label={props?.label || "Fecha"}>
                            <DateDay value={state[key]} onChange={(v) => set(key, v)} />
                        </Field>
                    );
                    if (type === "datetime") return (
                        <Field key={key} label={props?.label || "Fecha y hora fin"}>
                            <DateTime value={state[key]} onChange={(v) => set(key, v)} />
                        </Field>
                    );
                    if (type === "select") return (
                        <Field key={key} label={props?.label || props?.placeholder || "Seleccione"}>
                            <select
                                className={`w-full rounded border px-3 py-2 ${props?.className || ""}`}
                                value={state[key] || ""}
                                onChange={(e) => set(key, e.target.value)}
                            >
                                <option value="">{props?.placeholder || "Selecciona..."}</option>
                                {(props?.options || []).map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                            </select>
                        </Field>
                    );
                    // text por default
                    return (
                        <input
                            key={key}
                            className={`w-full rounded border px-3 py-2 ${props?.className || ""}`}
                            placeholder={props?.placeholder || key}
                            value={state[key] || ""}
                            onChange={(e) => set(key, e.target.value)}
                            type="text"
                        />
                    );
                })}
            </div>
        </div>
    );
}

/* ============ Card re-usable ============ */
function OperationCard({ id, schema, open, onToggle, children }) {
    const Icon = schema.icon;
    return (
        <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
            <button type="button" className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50" onClick={onToggle}>
                <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded ${schema.color}`}>
                        <Icon className="text-slate-700" />
                    </span>
                    <div className="text-left">
                        <div className="font-semibold text-slate-800 text-sm">{schema.label}</div>
                        <div className="text-[11px] text-slate-500">{schema.description}</div>
                    </div>
                </div>
                <span className="text-slate-400">{open ? <FaChevronUp /> : <FaChevronDown />}</span>
            </button>
            {open && children}
        </div>
    );
}


/* ============ Componente principal ============ */
export default function BalanceAdjustments({ empresaId, clientes = [], onDone, onClose }) {
    const [openId, setOpenId] = useState(null);
    const [loading, setLoading] = useState(false);

    // estado sencillo por operación
    const [form, setForm] = useState({
        manual_payment: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
        manual_charge: { amount: "", service: "", date: HOY, visible_comment: "", internal_comment: "" },
        manual_credit: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
        promotional_credit: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
        refund: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
        temp_credit_limit: { amount: "", end: "", visible_comment: "", internal_comment: "" },
        deposit_transfer: { from_cliente_id: "", to_cliente_id: "", amount: "", comment: "" },
    });

    const productos = []; // si luego los cargas, pásalos aquí
    const schemas = useMemo(() => OPS(empresaId, clientes, productos), [empresaId, clientes]);

    const toggle = (id) => setOpenId((p) => (p === id ? null : id));
    const isFilled = (id) => schemas[id].isFilled(form[id]);

    const countFilled = () =>
        Object.keys(schemas).reduce((acc, k) => acc + (isFilled(k) ? 1 : 0), 0);

    const triggerRefresh = () =>
        window.dispatchEvent(new CustomEvent("finanzas-changed", { detail: { empresa_id: Number(empresaId) } }));

    const applyOne = async (id) => {
        if (!empresaId) return toast.error("Empresa inválida.");
        const schema = schemas[id];
        if (!schema.isFilled(form[id])) return toast.warn("Completa los campos requeridos.");
        const built = typeof schema.build === "function" ? await schema.build(form[id]) : schema.build;
        await schema.call(built);
        toast.success("Ajuste aplicado.");

        // limpiar solo esa operación
        setForm((p) => ({ ...p, [id]: Object.fromEntries(Object.keys(p[id]).map(k => [k, ""])) }));
        setOpenId(null);
    };

    const applyAll = async () => {
        if (!empresaId) return toast.error("Empresa inválida.");
        const ids = Object.keys(schemas).filter((id) => isFilled(id));
        if (!ids.length) return toast.info("No hay ajustes por aplicar.");
        for (const id of ids) {
            const schema = schemas[id];
            const built = typeof schema.build === "function" ? await schema.build(form[id]) : schema.build;
            await schema.call(built);
        }
        toast.success("Todos los ajustes aplicados.");
        triggerRefresh();
        setForm({
            manual_payment: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
            manual_charge: { amount: "", service: "", date: HOY, visible_comment: "", internal_comment: "" },
            manual_credit: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
            promotional_credit: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
            refund: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
            temp_credit_limit: { amount: "", end: "", visible_comment: "", internal_comment: "" },
            deposit_transfer: { from_cliente_id: "", to_cliente_id: "", amount: "", comment: "" },
        });
        onDone?.(Number(empresaId));
        onClose?.();
    };

    const handleApply = async () => {
        try {
            setLoading(true);
            const filled = countFilled();
            if (filled === 1 && openId) await applyOne(openId);
            else await applyAll();
            triggerRefresh();
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "No se pudo aplicar el ajuste";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const CARDS = [
        "manual_payment",
        "manual_charge",
        "manual_credit",
        "promotional_credit",
        "refund",
        "temp_credit_limit",
        "deposit_transfer",
    ];

    return (
        <div className="space-y-4">
            {CARDS.map((id) => {
                const schema = schemas[id];
                return (
                    <OperationCard key={id} id={id} schema={schema} open={openId === id} onToggle={() => toggle(id)}>
                        <FormRenderer
                            schema={schema}
                            state={form[id]}
                            setState={(updater) => setForm((p) => ({ ...p, [id]: typeof updater === "function" ? updater(p[id]) : updater }))}
                            clientes={clientes}
                        />
                    </OperationCard>
                );
            })}

            {/* Cargos recurrentes (archivo aparte para mantener minimalismo aquí) */}
            <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
                <div className="w-full px-3 py-2 border-b bg-slate-50">
                    <div className="font-semibold text-slate-800 text-sm">Cargos Recurrentes</div>
                    <div className="text-[11px] text-slate-500">
                        Selecciona un producto y se autollenan descripción e importe.
                    </div>
                </div>

                <div className="p-4">
                    <CargoRecurrente
                        empresaId={empresaId}
                        onCreate={async (payload) => {
                            console.log('📤 Payload:', JSON.stringify(payload, null, 2));

                            try {
                                await axios.post(
                                    API_ROUTES.cargosRecurrentes.crear,
                                    payload,
                                    { withCredentials: true }
                                );
                                toast.success("Cargo recurrente creado exitosamente");
                                window.dispatchEvent(new CustomEvent("finanzas-changed", {
                                    detail: { empresa_id: Number(empresaId) }
                                }));
                            } catch (error) {
                                console.error('❌ Error backend:', error.response?.data);
                                const msg = error?.response?.data?.message ||
                                    error?.message ||
                                    "Error al crear cargo recurrente";
                                toast.error(msg);
                                throw error;
                            }
                        }}
                    />
                </div>
            </div>

            <div className="flex items-center justify-between pt-2">
                <button
                    type="button"
                    className="px-3 py-2 text-sm border rounded"
                    onClick={() =>
                        setForm({
                            manual_payment: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
                            manual_charge: { amount: "", service: "", date: HOY, visible_comment: "", internal_comment: "" },
                            manual_credit: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
                            promotional_credit: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
                            refund: { amount: "", date: HOY, visible_comment: "", internal_comment: "" },
                            temp_credit_limit: { amount: "", end: "", visible_comment: "", internal_comment: "" },
                            deposit_transfer: { from_cliente_id: "", to_cliente_id: "", amount: "", comment: "" },
                        })
                    }
                >
                    Limpiar formulario
                </button>

                <div className="flex gap-2">
                    <button type="button" className="px-3 py-2 text-sm border rounded" onClick={onClose}>Cerrar</button>
                    <button
                        type="button"
                        disabled={loading || countFilled() === 0}
                        className="px-3 py-2 text-sm rounded bg-blue-600 text-white disabled:opacity-50"
                        onClick={handleApply}
                    >
                        Aplicar
                    </button>
                </div>
            </div>
        </div>
    );
}
